import React, { memo, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Button, Tooltip, Space, Divider } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMicrophone } from '@fortawesome/free-solid-svg-icons'
import { faClock, faFile } from '@fortawesome/free-regular-svg-icons'

const ClinicalNotesToolbar = ({
  setShowClinicalNotesTemplates,
  noBooking,
  handleCreateNote,
  setShowAiTranscription,
  setShowHistorical,
  noteState,
  handleShowAIGenerateModal
}) => {
  const isDisabled = useMemo(() => {
    return (
      noteState.isAutoGenerating ||
      noteState.updatingError ||
      noteState.fetchingError ||
      noteState.isUpdating ||
      noteState.isCreating ||
      noteState.isSubmitting ||
      !noteState.noteId
    )
  }, [noteState])

  if (noBooking) return null

  const toolbarButtonStyle = {
    border: 'none',
    boxShadow: 'none',
    background: 'transparent',
    color: 'inherit',
    padding: '4px 8px',
    height: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }

  const iconStyle = {
    fontSize: '14px',
    fontWeight: '400'
  }

  return (
    <Space size={8} className='clinical-notes-toolbar'>
      {/* History Section */}
      <Tooltip title='View previously saved notes for this patient' placement='bottom'>
        <Button
          disabled={isDisabled}
          style={toolbarButtonStyle}
          onClick={() => setShowHistorical(true)}
          type='text'
          size='small'
        >
          <FontAwesomeIcon icon={faClock} style={iconStyle} />
        </Button>
      </Tooltip>

      {/* Divider between History and Editing */}
      <Divider type='vertical' style={{ height: '16px', margin: '0 4px' }} />

      {/* Editing Tools Section */}
      <Space size={4}>
        {/* Templates */}
        <Tooltip title='Access and insert pre-defined note templates' placement='bottom'>
          <Button
            disabled={isDisabled}
            style={toolbarButtonStyle}
            onClick={() => setShowClinicalNotesTemplates(true)}
            type='text'
            size='small'
          >
            <FontAwesomeIcon icon={faFile} style={iconStyle} />
          </Button>
        </Tooltip>

        {/* Voice Dictation */}
        <Tooltip title='Speech-to-text: Convert your voice directly into text' placement='bottom'>
          <Button
            disabled={isDisabled}
            style={toolbarButtonStyle}
            onClick={() => {
              // This will be handled by the dictaphone component
              // For now, we'll show a placeholder
            }}
            type='text'
            size='small'
          >
            <FontAwesomeIcon icon={faMicrophone} style={iconStyle} />
          </Button>
        </Tooltip>
      </Space>

      {/* Divider between Editing and AI Tools */}
      <Divider type='vertical' style={{ height: '16px', margin: '0 4px' }} />

      {/* AI Tools Section */}
      <Space size={12}>
        {/* AI Generate Note */}
        <Tooltip title='Use AI to automatically generate a clinical note based on patient data' placement='bottom'>
          <Button
            style={{
              ...toolbarButtonStyle,
              fontSize: '12px',
              fontWeight: '500',
              color: '#ffffff',
              backgroundColor: '#1890ff',
              padding: '4px 12px',
              borderRadius: '4px',
              border: '1px solid #1890ff',
              boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
            }}
            className='no-hover-effect'
            disabled={isDisabled}
            onClick={handleShowAIGenerateModal}
            type='primary'
            size='small'
          >
            AI Note
          </Button>
        </Tooltip>

        {/* AI Transcribe */}
        <Tooltip title='Record audio and have AI transcribe and format it into a clinical note' placement='bottom'>
          <Button
            disabled={isDisabled}
            style={{
              ...toolbarButtonStyle,
              fontSize: '12px',
              fontWeight: '500',
              color: '#ffffff',
              backgroundColor: '#1890ff',
              padding: '4px 12px',
              borderRadius: '4px',
              border: '1px solid #1890ff',
              boxShadow: '0 2px 4px rgba(24, 144, 255, 0.2)'
            }}
            className='no-hover-effect'
            onClick={() => setShowAiTranscription(true)}
            type='primary'
            size='small'
          >
            Transcribe AI
          </Button>
        </Tooltip>
      </Space>
    </Space>
  )
}

ClinicalNotesToolbar.propTypes = {
  setShowClinicalNotesTemplates: PropTypes.func.isRequired,
  noBooking: PropTypes.bool,
  handleCreateNote: PropTypes.func.isRequired,
  setShowAiTranscription: PropTypes.func.isRequired,
  setShowHistorical: PropTypes.func.isRequired,
  noteState: PropTypes.object.isRequired,
  handleShowAIGenerateModal: PropTypes.func.isRequired
}

export default memo(ClinicalNotesToolbar)
